import findResult, {
  assignIfExists,
  assignProps,
  mapItemOrderByValues,
  mapResults,
  removeEmpty,
  removeItemByCode,
  selectableResult,
} from '@/utils/resultsHelper';
import { responseError } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import { pruneFormFieldErrors } from '@/forms/state/state';
import initialState, { findTaskTemplate } from '../templates/state/state';
import {
  activeRequestTask,
  assignTaskEditProps,
  findAllRequestTasks,
  findRequestTask,
  findRequestTasks,
  findRequestTaskViews,
  manageRequestTask, pruneManageTaskErrors,
  requestTaskStateToLoad,
  requestTaskViews,
  tasksResult,
} from './state';
import actions, { requestTaskPermissions } from '../constants';

const fetchTasks = withDraft((draft, action) => {
  const item = findRequestTasks(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
});

const showTasks = withDraft((draft, action) => {
  const item = findRequestTasks(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results = action.append
    ? mapResults(item.results, action, tasksResult)
    : mapResults([], action, tasksResult);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
  item.view = action.view;
});

const showTasksError = withDraft((draft, action) => {
  const item = findRequestTasks(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const updateTasksFilter = withDraft((draft, action) => {
  const item = findRequestTasks(draft, action);
  item.filter = action.value;
});

const updateTasksViewFilter = withDraft((draft, action) => {
  const item = findRequestTasks(draft, action);
  item.view = action.view;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.currentPage = 1;
});

const closeTasks = withDraft((draft, { category }) => {
  draft.tasks[category] = requestTaskStateToLoad(draft.tasks).tasks[category];
  draft.tasks.views[category] = requestTaskViews();
});

const toggleTaskColumnOrderBy = withDraft((draft, { category, orderByColumn }) => {
  const item = findRequestTasks(draft, { category });
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.orderBy = mapItemOrderByValues(item.orderBy, orderByColumn, item.singleSorting);
});

const showViews = withDraft((draft, action) => {
  const item = findRequestTaskViews(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results = action.append
    ? mapResults(item.results, action, selectableResult)
    : mapResults([], action, selectableResult);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
});

const showViewsError = withDraft((draft, action) => {
  const item = findRequestTaskViews(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const toggleTasksPresentation = withDraft((draft, action) => {
  const item = findRequestTasks(draft, action);
  item.display = action.display;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.results = [];
  item.count = 0;
  item.currentPage = 1;
  item.totalPages = 0;
});

const fetchActiveRequestTask = withDraft((draft) => {
  const { activeTask } = draft.tasks;
  activeTask.readErrors = [];
  activeTask.readStatus = states.PENDING;
});

const ShowActiveRequestTask = withDraft((draft, { task, request }) => {
  const { activeTask } = draft.tasks;

  if (activeTask.open && activeTask.code === task.code) {
    const template = findTaskTemplate(draft, { category: 'open', code: task.formKey });

    assignProps(activeTask, task);
    if (template && task.permissions[requestTaskPermissions.UPDATE]) {
      assignTaskEditProps(activeTask, template, true);
    }
    activeTask.readStatus = states.SUCCESS;
    activeTask.readErrors = [];
    draft.requests.tasks.results = [request];
  }
});

const ShowActiveRequestTaskError = withDraft((draft, action) => {
  const { activeTask } = draft.tasks;

  if (activeTask.open && activeTask.code === action.code) {
    activeTask.readStatus = states.ERROR;
    activeTask.readErrors = [responseError(action)];
  }
});

const hideManageTask = withDraft((draft) => { draft.tasks.manageTask = manageRequestTask(); });

const hideRequestTask = withDraft((draft) => {
  draft.tasks.activeTask = activeRequestTask();
  draft.tasks.templates.open = initialState.templates.open;
  draft.requests.tasks.results = [];
});

const showRequestTask = withDraft((draft, { task }) => {
  const { activeTask } = draft.tasks;
  assignProps(activeTask, task);
  activeTask.open = true;
  activeTask.readStatus = states.NONE;
  activeTask.readErrors = [];
  draft.tasks.templates.open = initialState.templates.open;
  draft.requests.tasks.results = [];
});

const showManageRequestTask = withDraft((draft, { task }) => {
  const { manageTask } = draft.tasks;
  assignProps(manageTask, task);
  manageTask.open = true;
  manageTask.readStatus = states.NONE;
  manageTask.readErrors = [];
});

const updateRequestTasksTab = withDraft((draft, { step }) => {
  draft.tasks.activeTask.activeStepIndex = step;
});

const updateRequestTaskProperties = withDraft((draft, { properties, fields, entity }) => {
  const { activeTask } = draft.tasks;
  // assignEditProps(activeTask, template);
  assignProps(activeTask, { fields: properties, isDirty: true });
  activeTask.saveErrors = pruneFormFieldErrors(activeTask.saveErrors, { fields, entity });
});

const claimTask = withDraft((draft, action) => {
  const { activeTask } = draft.tasks;
  const item = activeTask.open ? activeTask : findRequestTask(draft, action);
  item.claimStatus = states.PENDING;
  item.saveErrors = [];
});

const manageTaskState = withDraft((draft) => {
  const { manageTask } = draft.tasks;
  manageTask.saveStatus = states.PENDING;
  manageTask.saveErrors = [];
});

const showUpdateRequestTaskSuccess = withDraft((draft, action) => {
  const { activeTask } = draft.tasks;
  const items = findAllRequestTasks(draft, { items: [action] }).map((category) => (
    findResult(draft.tasks, category, action.code)
  ));
  const openTask = activeTask.open && activeTask;
  const closeAndReset = openTask && [openTask.saveStatus, openTask.completeStatus].includes(states.PENDING);
  const props = {
    ...action.result,
    claimStatus: states.NONE,
    completeStatus: states.NONE,
    saveStatus: states.SUCCESS,
    saveErrors: [],
  };

  if (closeAndReset) {
    draft.tasks.activeTask = activeRequestTask();
  } else { items.push(openTask); }

  removeEmpty(items).forEach((item) => { assignIfExists(item, props); });
});

const completeRequestTasksSuccess = withDraft((draft, { items }) => {
  draft.tasks.activeTask = activeRequestTask();
  findAllRequestTasks(draft, { items }).forEach((category) => {
    items.forEach(({ code }) => {
      const item = findResult(draft.tasks, category, code);
      if (item) {
        draft.tasks[category].results = removeItemByCode(draft.tasks[category].results, item);
      }
    });
  });
});

const showUpdateRequestTaskError = withDraft((draft, action) => {
  const { activeTask } = draft.tasks;
  const item = activeTask.open ? activeTask : findRequestTask(draft, action);

  assignIfExists(item, {
    claimStatus: states.NONE,
    completeStatus: states.NONE,
    saveStatus: states.ERROR,
    saveErrors: [responseError(action)],
  });
});

const completeTask = withDraft((draft) => {
  const { activeTask } = draft.tasks;

  activeTask.completeStatus = states.PENDING;
  activeTask.saveErrors = [];
});

const saveTask = withDraft((draft) => {
  const { activeTask } = draft.tasks;

  activeTask.saveStatus = states.PENDING;
  activeTask.saveErrors = [];
});

const updateActiveRequestProperties = withDraft((draft, { properties }) => {
  const { activeTask } = draft.tasks;
  assignIfExists(activeTask, properties);
  activeTask.isDirty = true;
});

const updateManageRequestProperties = withDraft((draft, { properties }) => {
  const { manageTask } = draft.tasks;
  assignIfExists(manageTask, properties);
  manageTask.saveErrors = pruneManageTaskErrors(manageTask.saveErrors, { properties });
  manageTask.isDirty = true;
});

const showManageRequestTaskError = withDraft((draft, action) => {
  const { manageTask } = draft.tasks;

  assignIfExists(manageTask, {
    saveStatus: states.ERROR,
    saveErrors: [responseError(action)],
  });
});

const showManageRequestTaskSuccess = withDraft((draft, { code, result }) => {
  draft.tasks.manageTask = manageRequestTask();
  const props = { ...result, saveStatus: states.SUCCESS, saveErrors: [] };
  findAllRequestTasks(draft, { items: [{ code }] }).forEach((category) => {
    const item = findResult(draft.tasks, category, code);
    assignIfExists(item, props);
  });
});

export default {
  [actions.CLAIM_REQUEST_TASK]: claimTask,
  [actions.COMPLETE_REQUEST_TASK]: completeTask,
  [actions.COMPLETE_REQUEST_TASKS_SUCCESS]: completeRequestTasksSuccess,
  [actions.CLOSE_REQUEST_TASKS]: closeTasks,
  [actions.FETCH_ACTIVE_REQUEST_TASK]: fetchActiveRequestTask,
  [actions.FETCH_REQUEST_TASKS]: fetchTasks,
  [actions.HIDE_MANAGE_TASK]: hideManageTask,
  [actions.HIDE_REQUEST_TASK]: hideRequestTask,
  [actions.MANAGE_REQUEST_TASK]: manageTaskState,
  [actions.REFRESH_REQUEST_TASKS]: fetchTasks,
  [actions.SAVE_REQUEST_TASK]: saveTask,
  [actions.SHOW_ACTIVE_REQUEST_TASK]: ShowActiveRequestTask,
  [actions.SHOW_ACTIVE_REQUEST_TASK_ERROR]: ShowActiveRequestTaskError,
  [actions.SHOW_MANAGE_REQUEST_TASK]: showManageRequestTask,
  [actions.SHOW_MANAGE_REQUEST_TASK_ERROR]: showManageRequestTaskError,
  [actions.SHOW_MANAGE_REQUEST_TASK_SUCCESS]: showManageRequestTaskSuccess,
  [actions.SHOW_REQUEST_TASK]: showRequestTask,
  [actions.SHOW_REQUEST_TASK_VIEWS]: showViews,
  [actions.SHOW_REQUEST_TASK_VIEWS_ERROR]: showViewsError,
  [actions.SHOW_REQUEST_TASKS]: showTasks,
  [actions.SHOW_REQUEST_TASKS_ERROR]: showTasksError,
  [actions.SHOW_UPDATE_REQUEST_TASK_ERROR]: showUpdateRequestTaskError,
  [actions.SHOW_UPDATE_REQUEST_TASK_SUCCESS]: showUpdateRequestTaskSuccess,
  [actions.TOGGLE_REQUEST_TASK_COLUMN_ORDER_BY]: toggleTaskColumnOrderBy,
  [actions.UPDATE_ACTIVE_TASK_PROPERTIES]: updateActiveRequestProperties,
  [actions.UPDATE_MANAGE_TASK_PROPERTIES]: updateManageRequestProperties,
  [actions.UPDATE_REQUEST_TASK_FORM_PROPERTIES]: updateRequestTaskProperties,
  [actions.UPDATE_REQUEST_TASK_TAB]: updateRequestTasksTab,
  [actions.TOGGLE_REQUEST_TASKS_PRESENTATION]: toggleTasksPresentation,
  [actions.UPDATE_REQUEST_TASKS_FILTER]: updateTasksFilter,
  [actions.UPDATE_REQUEST_TASKS_VIEW_FILTER]: updateTasksViewFilter,
};
